import { createSlice } from "@reduxjs/toolkit";
import {
  fetchWpCarDetails,
  fetchMcCarDetails,
  fetchWpVendors,
  fetchRealtyHouseDetails,
} from "../thunks/detailsThunks";
import {
  fetchCurrUser,
  replyListing,
  writeReview,
  reportListing,
} from "../thunks/usersThunks";
import {
  checkIfFavorited,
  addFavorite,
  removeFavorite,
} from "../thunks/favoritesThunks";
import { fetchRecipientId, fetchFormId } from "../thunks/vendorsThunks";
const initialState = {
  user: null,
  data: [],
  originalData: [],
  sorted_data: [],
  originalSorted_data: [],
  carDetails: [],
  vendorDetails: [],
  filters: {
    location: {
      lat: "33.7488",
      lng: "-84.3877",
      radius: 100,
    },
    price_range: {
      min: 0,
      max: 500000,
    },
    year_range: {
      min: 1990,
      max: 2025,
    },
    mile_range: {
      min: 0,
      max: 500000,
    },
    make: "",
    model: "",
    condition: null,
    extColors: [],
    intColors: [],
    transmission: [],
    drive: [],
    engine: [],
    body: [],
    fuel: [],
    premium_features: [],
    seller: null,
    cleanTitle: null,
  },
  searchTerm: "",
  sortBy: "Closest to Me",
  formId: null,
  isFilter: false,
  isLoading: false,
  isFavoriteLoading: false,
  isError: null,
};

const details = createSlice({
  name: "details",
  initialState,
  reducers: {
    setUser(state, action) {
      state.user = action.payload;
    },
    setFormId(state, action) {
      state.formId = action.payload;
    },
  },
  extraReducers(builder) {
    // FETCH CURRENT USER
    builder.addCase(fetchCurrUser.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(fetchCurrUser.fulfilled, (state, action) => {
      state.user = action.payload;
      state.isLoading = false;
    });
    builder.addCase(fetchCurrUser.rejected, (state, action) => {
      state.isLoading = false;
      console.log(action.error);
    });

    // CHECK IF FAVORITED
    builder.addCase(checkIfFavorited.pending, (state, action) => {
      state.isFavoriteLoading = true;
    });
    builder.addCase(checkIfFavorited.fulfilled, (state, action) => {
      state.isFavoriteLoading = false;
    });
    builder.addCase(checkIfFavorited.rejected, (state, action) => {
      state.isFavoriteLoading = false;
      console.log(action.error);
    });

    // ADD FAVORITES
    builder.addCase(addFavorite.pending, (state, action) => {
      state.isFavoriteLoading = true;
    });
    builder.addCase(addFavorite.fulfilled, (state, action) => {
      state.isFavoriteLoading = false;
    });
    builder.addCase(addFavorite.rejected, (state, action) => {
      state.isFavoriteLoading = false;
      console.log(action.error);
    });

    // REMOVE FAVORITES
    builder.addCase(removeFavorite.pending, (state, action) => {
      state.isFavoriteLoading = true;
    });
    builder.addCase(removeFavorite.fulfilled, (state, action) => {
      state.isFavoriteLoading = false;
    });
    builder.addCase(removeFavorite.rejected, (state, action) => {
      state.isFavoriteLoading = false;
      console.log(action.error);
    });

    // REPLY TO LISTING
    builder.addCase(replyListing.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(replyListing.fulfilled, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(replyListing.rejected, (state, action) => {
      state.isLoading = false;
      console.log(action.error);
    });

    // WRITE A REVIEW
    builder.addCase(writeReview.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(writeReview.fulfilled, (state, action) => {
      state.isLoading = false;
      const currentRating = Number(state.vendorDetails.meta.hp_rating);
      const totalRatings = Number(state.vendorDetails.meta.hp_rating_count);
      const newRating = Number(action.payload.rating);

      const newTotalScore = currentRating * totalRatings + newRating;
      const newTotalRatings = totalRatings + 1;
      const newAverageRating = newTotalScore / newTotalRatings;

      state.vendorDetails.meta.hp_rating = newAverageRating;
      state.vendorDetails.meta.hp_rating_count = newTotalRatings;
    });
    builder.addCase(writeReview.rejected, (state, action) => {
      state.isLoading = false;
      console.log(action.error);
    });

    // GET RECIPIENT ID
    builder.addCase(fetchRecipientId.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(fetchRecipientId.fulfilled, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(fetchRecipientId.rejected, (state, action) => {
      state.isLoading = false;
    });

    // GET FORM ID
    builder.addCase(fetchFormId.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(fetchFormId.fulfilled, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(fetchFormId.rejected, (state, action) => {
      state.isLoading = false;
      console.log(action.error);
    });

    // REPORT LISTING
    builder.addCase(reportListing.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(reportListing.fulfilled, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(reportListing.rejected, (state, action) => {
      state.isLoading = false;
      console.log(action.error);
    });

    // FETCH WP CAR DETAILS
    builder.addCase(fetchWpCarDetails.pending, (state, action) => {
      state.isLoading = true;
    });

    builder.addCase(fetchWpCarDetails.fulfilled, (state, action) => {
      state.isLoading = false;
      state.carDetails = action.payload.data;
    });

    builder.addCase(fetchWpCarDetails.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
    });

    // FETCH MC CAR DETAILS
    builder.addCase(fetchMcCarDetails.pending, (state, action) => {
      state.isLoading = true;
    });

    builder.addCase(fetchMcCarDetails.fulfilled, (state, action) => {
      state.isLoading = false;
      state.carDetails = action.payload.data;
    });

    builder.addCase(fetchMcCarDetails.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
    });

    // FETCH HOUSE DETAILS
    builder.addCase(fetchRealtyHouseDetails.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(fetchRealtyHouseDetails.fulfilled, (state, action) => {
      state.isLoading = false;
      state.carDetails = action.payload.data.data;
    });
    builder.addCase(fetchRealtyHouseDetails.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
    });

    // FETCH WP VENDORS
    builder.addCase(fetchWpVendors.pending, (state, action) => {
      state.isLoading = true;
    });

    builder.addCase(fetchWpVendors.fulfilled, (state, action) => {
      state.isLoading = false;
      state.vendorDetails = action.payload.data;
    });

    builder.addCase(fetchWpVendors.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
    });
  },
});

export const detailsReducer = details.reducer;
export const { setUser, setFormId } = details.actions;
